import { useMemo, useState, type FormEvent } from "react";
import {
  FAILURE_MODES,
  FAILURE_SERVICES,
  MAX_COUNT,
  MAX_DURATION_SECONDS,
  generateLoad,
  type FailureMode,
  type FailureService,
  type LoadRequest,
  type OrderItem,
  type RunResponse,
} from "../run";
import { withSpan } from "../otel";
import { OrderNrLinks } from "../NrLinks";
import type { NrConfig } from "../newrelic";
import { loadNrConfig } from "../storage";
import { signIn } from "../session";

// Run (#262, ADR 0064) — absorbed from load-generator-ui's "Run" tab: fire
// fake order load through order-service, same request shape
// (services/load-generator/openapi.yaml). Behind the BFF now, so the
// Connection fieldset (API base URL + shared secret) that tab needed is
// gone — cookie auth, same origin. The live per-order tracker and the
// Orders/Stock/Guide tabs that tab also had aren't ported: this screen's
// Orders equivalent is the Orders screen itself (#258), one tab over.

interface ItemRow extends OrderItem {
  key: number;
}

let itemKeySeq = 1;

interface Preset {
  label: string;
  count: string;
  forceFailure?: { service: FailureService; mode: FailureMode };
  hint: string;
}

const PRESETS: Preset[] = [
  {
    label: "Happy path",
    count: "10",
    hint: "10 orders, nothing forced — about 4 still fail on the injected 20%-per-stage rate.",
  },
  {
    label: "Payment outage",
    count: "10",
    forceFailure: { service: "payment-service", mode: "exception" },
    hint: "Payment fails every order; the inventory / shipping / fraud fan-out never runs.",
  },
  {
    label: "Inventory shortage",
    count: "10",
    forceFailure: { service: "inventory-service", mode: "partial" },
    hint: "Inventory fails while shipping + fraud succeed — a partial fan-out failure.",
  },
  {
    label: "Fraud spike",
    count: "10",
    forceFailure: { service: "fraud-check-service", mode: "exception" },
    hint: "Fraud-check flags every order; payment / inventory / shipping still succeed.",
  },
  {
    label: "Trace fragmentation",
    count: "5",
    forceFailure: { service: "shipping-service", mode: "timeout" },
    hint: "Shipping times out → DLQ after 2 retries. Redrive it to see the trace fork onto a new trace.id.",
  },
];

export function RunScreen() {
  const [count, setCount] = useState("1");
  const [durationSeconds, setDurationSeconds] = useState("");
  const [ratePerMinute, setRatePerMinute] = useState("");
  const [currency, setCurrency] = useState("");
  const [items, setItems] = useState<ItemRow[]>([]);
  const [failService, setFailService] = useState<"" | FailureService>("");
  const [failMode, setFailMode] = useState<"" | FailureMode>("");
  const [activePreset, setActivePreset] = useState<string | null>(null);

  // "unauthorized" is handled (and redirected) inline in run() below, never
  // stored — so what's rendered is always one of the other three kinds.
  type ShownResponse = Exclude<RunResponse, { kind: "unauthorized" }>;

  const [busy, setBusy] = useState(false);
  const [response, setResponse] = useState<ShownResponse | null>(null);
  const [transportError, setTransportError] = useState<string | null>(null);

  const nrConfig = loadNrConfig();

  function applyPreset(p: Preset) {
    setCount(p.count);
    setDurationSeconds("");
    setRatePerMinute("");
    setFailService(p.forceFailure?.service ?? "");
    setFailMode(p.forceFailure?.mode ?? "");
    setActivePreset(p.label);
  }

  const req = useMemo<LoadRequest>(() => {
    const r: LoadRequest = {};
    if (count.trim() !== "") r.count = Number(count);
    if (durationSeconds.trim() !== "") r.durationSeconds = Number(durationSeconds);
    if (ratePerMinute.trim() !== "") r.ratePerMinute = Number(ratePerMinute);

    const order: LoadRequest["order"] = {};
    if (currency.trim() !== "") order.currency = currency.trim();
    const cleanItems = items
      .filter((i) => i.sku.trim() !== "")
      .map(({ sku, quantity, unitPrice }) => ({ sku: sku.trim(), quantity: Number(quantity) || 1, unitPrice: Number(unitPrice) || 0 }));
    if (cleanItems.length > 0) order.items = cleanItems;
    if (Object.keys(order).length > 0) r.order = order;

    if (failService !== "" && failMode !== "") {
      r.forceFailure = { service: failService, mode: failMode };
    }
    return r;
  }, [count, durationSeconds, ratePerMinute, currency, items, failService, failMode]);

  const forceFailurePartial = (failService !== "") !== (failMode !== "");

  async function run(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setResponse(null);
    setTransportError(null);
    try {
      const res = await withSpan(
        "admin.generate-load",
        {
          "loadgen.count": req.count ?? 1,
          ...(req.forceFailure
            ? { "loadgen.force_failure.service": req.forceFailure.service, "loadgen.force_failure.mode": req.forceFailure.mode }
            : {}),
        },
        () => generateLoad(req),
      );
      if (res.kind === "unauthorized") {
        signIn(window.location.pathname);
        return;
      }
      setResponse(res);
    } catch (err) {
      setTransportError(err instanceof Error ? err.message : "request failed (network)");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="run-screen">
      <p className="sub">
        Fire fake order load through <code>order-service</code>. A single-order run is one trace through all five
        backend languages; a multi-order run keeps each order on its own trace.
      </p>

      <form onSubmit={run}>
        <fieldset>
          <legend>Scenarios</legend>
          <div className="presets">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                aria-pressed={activePreset === p.label}
                className={activePreset === p.label ? "active" : ""}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <p className="hint">
            {activePreset ? PRESETS.find((p) => p.label === activePreset)?.hint : "One click fills count + force-failure below. Tweak anything, then Run."}
          </p>
        </fieldset>

        <fieldset>
          <legend>Volume</legend>
          <div className="row">
            <label>
              count
              <input type="number" min={1} max={MAX_COUNT} value={count} onChange={(e) => setCount(e.target.value)} />
            </label>
            <label>
              durationSeconds
              <input
                type="number"
                min={0}
                max={MAX_DURATION_SECONDS}
                value={durationSeconds}
                placeholder="0 = burst"
                onChange={(e) => setDurationSeconds(e.target.value)}
              />
            </label>
            <label>
              ratePerMinute
              <input type="number" min={0} step="any" value={ratePerMinute} placeholder="optional" onChange={(e) => setRatePerMinute(e.target.value)} />
            </label>
          </div>
          <p className="hint">
            <code>ratePerMinute</code> replaces one of <code>count</code> / <code>durationSeconds</code> — supply it with exactly one of them.
          </p>
        </fieldset>

        <fieldset>
          <legend>Order override</legend>
          <label className="narrow">
            currency
            <input type="text" value={currency} placeholder="GBP" maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
          </label>

          <div className="items">
            <div className="items-head">
              <span>items</span>
              <button type="button" onClick={() => setItems((xs) => [...xs, { key: itemKeySeq++, sku: "", quantity: 1, unitPrice: 0 }])}>
                + add item
              </button>
            </div>
            {items.length === 0 && <p className="hint">None — the generator randomises line items.</p>}
            {items.map((it, idx) => (
              <div className="row item-row" key={it.key}>
                <label>
                  sku
                  <input
                    type="text"
                    value={it.sku}
                    onChange={(e) => setItems((xs) => xs.map((x, i) => (i === idx ? { ...x, sku: e.target.value } : x)))}
                  />
                </label>
                <label>
                  quantity
                  <input
                    type="number"
                    min={1}
                    value={it.quantity}
                    onChange={(e) => setItems((xs) => xs.map((x, i) => (i === idx ? { ...x, quantity: Number(e.target.value) } : x)))}
                  />
                </label>
                <label>
                  unitPrice
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={it.unitPrice}
                    onChange={(e) => setItems((xs) => xs.map((x, i) => (i === idx ? { ...x, unitPrice: Number(e.target.value) } : x)))}
                  />
                </label>
                <button type="button" className="remove" aria-label="remove item" onClick={() => setItems((xs) => xs.filter((_, i) => i !== idx))}>
                  &times;
                </button>
              </div>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend>Force failure (ADR 0005)</legend>
          <div className="row">
            <label>
              service
              <select value={failService} onChange={(e) => setFailService(e.target.value as "" | FailureService)}>
                <option value="">— none —</option>
                {FAILURE_SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label>
              mode
              <select value={failMode} onChange={(e) => setFailMode(e.target.value as "" | FailureMode)}>
                <option value="">— none —</option>
                {FAILURE_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {forceFailurePartial && (
            <p className="hint warn">
              Both <code>service</code> and <code>mode</code> are required together — this pair will be dropped.
            </p>
          )}
        </fieldset>

        <div className="actions">
          <button type="submit" disabled={busy}>
            {busy ? "Running…" : "Run"}
          </button>
        </div>
      </form>

      <section className="payload">
        <h3>Request body</h3>
        <pre>{JSON.stringify(req, null, 2)}</pre>
      </section>

      {transportError && (
        <section className="result error">
          <h3>Request failed</h3>
          <p>{transportError}</p>
        </section>
      )}

      {response && <RunResultView response={response} nrConfig={nrConfig} />}
    </div>
  );
}

function RunResultView({ response, nrConfig }: { response: Exclude<RunResponse, { kind: "unauthorized" }>; nrConfig: NrConfig }) {
  if (response.kind === "result") {
    const r = response.body;
    return (
      <section className="result ok">
        <h3>200 · run finished</h3>
        <dl className="stats">
          <div>
            <dt>requested</dt>
            <dd>{r.requested}</dd>
          </div>
          <div>
            <dt>dispatched</dt>
            <dd>{r.dispatched}</dd>
          </div>
          <div>
            <dt>failed</dt>
            <dd>{r.failed ?? 0}</dd>
          </div>
        </dl>
        {r.orderIds?.length > 0 && (
          <>
            <h3>orderIds</h3>
            <ul className="ids">
              {r.orderIds.map((id) => (
                <li key={id}>
                  <code>{id}</code> <OrderNrLinks orderId={id} cfg={nrConfig} />
                </li>
              ))}
            </ul>
          </>
        )}
        {r.errors && r.errors.length > 0 && (
          <>
            <h3>errors</h3>
            <ul className="errs">
              {r.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </>
        )}
      </section>
    );
  }

  if (response.kind === "async") {
    const a = response.body;
    return (
      <section className="result async">
        <h3>202 · handed off to an async run</h3>
        <p>
          {a.accepted} orders, mode <code>{a.mode}</code>, over {a.durationSeconds}s. Progress is in the load-generator&rsquo;s CloudWatch logs.
        </p>
      </section>
    );
  }

  return (
    <section className="result error">
      <h3>{response.status} · error</h3>
      <p>{response.body.error}</p>
    </section>
  );
}
